import { PIECES, footprint, topOf } from '../../shared/content/pieces.js';

// The main thread's handle on the physics worker (src/client/physics/worker.js): it sends what the
// player does and keeps the latest pose the worker answered with. Behind ?physics=1.
export class Physics {
  constructor() {
    this.ready = false;
    this.state = null; // the newest { x, y, z, qx, qy, qz, qw, speed, lean, wheels, t }
    this.lag = 0; // ms from the input that produced the newest state to its arrival
    this.worker = new Worker('physics.js');
    this.worker.onmessage = (e) => {
      const m = e.data;
      if (m.type === 'ready') this.ready = true;
      else if (m.type === 'state') {
        this.state = m;
        this.lag = performance.now() - m.t;
      }
    };
    this.worker.postMessage({ type: 'init' });
  }

  // A port the streaming worker posts cell geometry through, straight to the physics worker.
  streamPort() {
    const channel = new MessageChannel();
    this.worker.postMessage({ type: 'port', port: channel.port1 }, [channel.port1]);
    return channel.port2;
  }

  dropCell(key) {
    this.worker.postMessage({ type: 'dropCell', key });
  }

  // The solid boxes of a plot's built parts.
  setParts(key, parts) {
    const boxes = [];
    for (const part of parts) {
      const piece = PIECES[part[0]];
      if (piece.passable || piece.id === 'tree') continue;
      const f = footprint(part);
      const y0 = part[2] / 2;
      const y1 = topOf(part);
      boxes.push({ x: (f.x0 + f.x1) / 2, y: (y0 + y1) / 2, z: (f.z0 + f.z1) / 2, hx: (f.x1 - f.x0) / 2, hy: (y1 - y0) / 2, hz: (f.z1 - f.z0) / 2, yaw: 0 });
    }
    this.worker.postMessage({ type: 'parts', key, boxes });
  }

  dropParts(key) {
    this.worker.postMessage({ type: 'dropParts', key });
  }

  enter(kind, x, y, z, yaw) {
    this.state = null;
    this.worker.postMessage({ type: 'enter', kind, x, y, z, yaw });
  }

  exit() {
    this.worker.postMessage({ type: 'exit' });
    this.state = null;
  }

  // throttle -1..1, steer -1..1, brake 0/1, hand 0/1 (handbrake), grip: the surface's hold, dt seconds
  input(throttle, steer, brake, hand, grip, dt) {
    this.worker.postMessage({ type: 'input', throttle, steer, brake, hand, grip, dt, t: performance.now() });
  }
}

// Yaw (heading) and roll (lean) of a quaternion whose forward axis is +z, in radians.
export function yawRoll(qx, qy, qz, qw) {
  const fx = 2 * (qx * qz + qw * qy);
  const fz = 1 - 2 * (qx * qx + qy * qy);
  const yaw = Math.atan2(fx, fz);
  // the chassis's right axis (-x in this game's vehicle frame) tipped up or down
  const rx = 1 - 2 * (qy * qy + qz * qz);
  const ry = 2 * (qx * qy + qw * qz);
  const roll = Math.atan2(ry, Math.abs(rx) < 1e-6 ? 1e-6 : rx);
  return { yaw, roll };
}
